import { normalizeContainerObjectIdList } from "./installationContainerUtils";

export const DEFAULT_PRODUCTION_TIMINGS = [
  "0 6,12,20 * * *",
  "30 8,14 * * *",
  "39 10,16,18 * * *",
];

export const INSTALLATION_MODE_INTERVAL_MINUTES = 5;

export const BATTERY_PRESERVATION_TIMINGS = [
  "0 7 * * *",
  "30 9 * * *",
  "0 12 * * *",
  "30 14 * * *",
  "0 17 * * *",
];

export const INSTALLATION_LIST_STALE_KEY = "installation_list_stale";
export const INSTALLATION_DETAIL_STALE_KEY = "installation_detail_stale";

export function getBaseCameraId(cameraId) {
  return String(cameraId || "")
    .replace(/_l$/, "")
    .replace(/_r$/, "");
}

function isMobileAppCamera(cameraId) {
  return getBaseCameraId(cameraId).startsWith("ak_mobile_");
}

export function isVisibleInInstallationList(camera) {
  if (!camera?._id) {
    return false;
  }
  if (camera._id.endsWith("_r")) {
    return false;
  }
  return !isMobileAppCamera(camera._id);
}

export function getLeftCameraId(cameraId) {
  const base = getBaseCameraId(cameraId);
  if (cameraId.endsWith("_r")) {
    return `${base}_l`;
  }
  if (cameraId.endsWith("_l")) {
    return cameraId;
  }
  return `${base}_l`;
}

export function getRightCameraId(cameraId) {
  return `${getBaseCameraId(cameraId)}_r`;
}

export function normalizeTimings(configData) {
  if (!configData?.timings) {
    return [];
  }
  if (Array.isArray(configData.timings)) {
    return configData.timings.filter(Boolean);
  }
  if (Array.isArray(configData.timings?.NORMAL)) {
    return configData.timings.NORMAL.filter(Boolean);
  }
  return [];
}

export function getTimingsSave(configData) {
  if (Array.isArray(configData?.TIMINGS_SAVE)) {
    return configData.TIMINGS_SAVE.filter(Boolean);
  }
  if (Array.isArray(configData?.TIMINGS_SAVE?.NORMAL)) {
    return configData.TIMINGS_SAVE.NORMAL.filter(Boolean);
  }
  return null;
}

export function isInstallationCronTimings(timings) {
  return (
    Array.isArray(timings) &&
    timings.length === 1 &&
    /^\*\/\d+/.test(String(timings[0]).trim())
  );
}

function normalizeCronLinesForComparison(lines) {
  return (lines || [])
    .map((line) => String(line).trim())
    .sort()
    .join("|");
}

export function isBatteryPreservationCronTimings(timings) {
  if (!Array.isArray(timings)) {
    return false;
  }
  return (
    normalizeCronLinesForComparison(timings) ===
    normalizeCronLinesForComparison(BATTERY_PRESERVATION_TIMINGS)
  );
}

export function buildTimingsAzurePartial(
  configData,
  activeTimings,
  timingsSave,
) {
  return {
    timings: timingsToConfigShape(activeTimings, configData),
    TIMINGS_SAVE: timingsSave,
  };
}

function timingsToConfigShape(existingTimings, configData) {
  const next = [...existingTimings];
  if (configData?.timings && !Array.isArray(configData.timings)) {
    return { ...configData.timings, NORMAL: next };
  }
  return next;
}

export function mergeAzureConfig(currentConfig, partial) {
  return { ...currentConfig, ...partial };
}

export function buildInstallationCronExpression(intervalMinutes) {
  return `*/${intervalMinutes} * * * *`;
}

export function isValidCronLine(line) {
  const trimmed = String(line || "").trim();
  if (!trimmed) {
    return false;
  }
  return trimmed.split(/\s+/).length === 5;
}

function resolveAddressFields(data) {
  if (Array.isArray(data.address) && data.address.length > 0) {
    return {
      addressId: data.address[0]?._id ?? null,
      addressName: data.address[0]?.name ?? "",
    };
  }
  if (data.address && typeof data.address === "object") {
    return {
      addressId: data.address._id ?? null,
      addressName: data.address.name ?? "",
    };
  }
  if (typeof data.address === "string" && data.address) {
    return {
      addressId: data.address,
      addressName: data.addressName ?? "",
    };
  }
  return {
    addressId: data.addressId ?? null,
    addressName: data.addressName ?? "",
  };
}

export function flattenCameraRecord(data) {
  if (!data) {
    return null;
  }
  const hardwareComponents = data.hardwareComponents || {};
  const { addressId, addressName } = resolveAddressFields(data);
  return {
    _id: data._id,
    side: data.side,
    active: data.active,
    name: data.name,
    addressId,
    addressName,
    corporationId: data.corporation?._id || data.corporation || null,
    corporationName:
      data.corporationName || data.corporation?.corporationName || "",
    iccid: data.iccid,
    status: data.status,
    image_rotation_angle: data.image_rotation_angle ?? 0,
    maskRegionCount: Array.isArray(data.masks?.regions)
      ? data.masks.regions.length
      : 0,
    containerObjectIdList: normalizeContainerObjectIdList(
      data.containerObjectIdList,
    ),
    codeVersion: hardwareComponents.codeVersion,
    caseVersion: hardwareComponents.caseVersion,
  };
}

export function resolveAddressFromSources(leftCamera, cameraListItem) {
  const fromList = cameraListItem?.address?.[0];
  const addressId = leftCamera?.addressId || fromList?._id || null;
  const addressName =
    leftCamera?.addressName?.trim() ||
    (fromList?._id === addressId ? fromList?.name : "") ||
    "";
  return { addressId, addressName };
}

export function flattenCameraListItem(item) {
  if (!item) {
    return null;
  }
  return flattenCameraRecord({
    ...item,
    corporation: item.corporation || { _id: item.corporationId },
  });
}

export function canEditMeasurementSchedule(codeVersion) {
  const parsed = parseFloat(codeVersion);
  if (Number.isNaN(parsed)) {
    return false;
  }
  return Math.floor(parsed) >= 3;
}

export const AZURE_RAW_IMAGE_QUERY =
  "?sp=racwdl&st=2022-07-26T12:47:17Z&se=2032-04-01T20:47:17Z&spr=https&sv=2021-06-08&sr=c&sig=s%2BcYHrtVj4IStYaCALzDcMLmoDsJH%2B6FfBFpCYHOBO4%3D";

export const INSTALLATION_CAMERA_STATUSES = [
  "working",
  "test",
  "production",
  "broken",
  "dead",
];

const BLANK_NETWORK_FIELD_VALUES = new Set([
  "",
  "none",
  "null",
  "undefined",
  "nan",
]);

export function isBlankNetworkField(value) {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return BLANK_NETWORK_FIELD_VALUES.has(normalized);
}

function sanitizeNetworkField(value) {
  return isBlankNetworkField(value) ? null : String(value).trim();
}

export function sanitizeNetworkDetails(networkDetails) {
  if (!networkDetails) {
    return null;
  }
  const networkQuality = sanitizeNetworkField(networkDetails.networkQuality);
  const operator = sanitizeNetworkField(networkDetails.operator);
  if (!networkQuality && !operator) {
    return null;
  }
  return { networkQuality, operator };
}

export function getNetworkQualityLabel(t, networkQuality) {
  if (isBlankNetworkField(networkQuality)) {
    return t("installation_network_quality_unknown");
  }
  const key = `installation_network_quality_${String(networkQuality).toLowerCase()}`;
  const translated = t(key);
  if (translated !== key) {
    return translated;
  }
  return t("installation_network_quality_unknown");
}

export function formatNetworkOperatorLabel(t, operator) {
  if (isBlankNetworkField(operator)) {
    return t("installation_state_network_operator_unknown");
  }
  return String(operator).trim();
}

export function formatNetworkSubtitle(networkDetails, t) {
  const sanitized = sanitizeNetworkDetails(networkDetails);
  if (!sanitized) {
    return t("installation_no_recent_log");
  }

  const parts = [];
  if (sanitized.networkQuality) {
    parts.push(getNetworkQualityLabel(t, sanitized.networkQuality));
  }
  if (sanitized.operator) {
    parts.push(formatNetworkOperatorLabel(t, sanitized.operator));
  }
  return parts.join(" · ");
}

export function normalizeSimProvider(provider) {
  const normalized = String(provider || "")
    .trim()
    .toLowerCase();
  if (normalized === "cellhire" || normalized === "matooma") {
    return normalized;
  }
  return null;
}

export function getInstallationCameraStatusLabel(t, status) {
  if (!status || status === "undefined") {
    return t("installation_camera_status_undefined");
  }
  const key = `installation_camera_status_${status}`;
  const translated = t(key);
  if (translated !== key) {
    return translated;
  }
  return status;
}

export function formatRotationAngle(angle) {
  const normalized = (((Number(angle) || 0) % 360) + 360) % 360;
  return `${normalized}°`;
}

export function isInstallationSide(side) {
  return side === "left" || side === "right";
}

export function getCameraForSide(leftCamera, rightCamera, side) {
  if (side === "right") {
    return rightCamera;
  }
  return leftCamera;
}

function getPhotoConfigKey(side) {
  return side === "right" ? "photoR" : "photoL";
}

export function isPhotoSideEnabled(configData, side) {
  const value = configData?.[getPhotoConfigKey(side)];
  if (value === 0 || value === false) {
    return false;
  }
  return true;
}

export function formatPhotoCaptureStatus(t, configData, side) {
  return isPhotoSideEnabled(configData, side)
    ? t("installation_photo_active")
    : t("installation_photo_inactive");
}

export function buildPhotoAzureOutput(configData, side, enabled) {
  const key = getPhotoConfigKey(side);
  const output = { ...configData };

  if (enabled) {
    delete output[key];
  } else {
    output[key] = 0;
  }

  return output;
}

function getCropConfigKey(side) {
  return side === "right" ? "polygone_crop_right" : "polygone_crop_left";
}

function getCropPolygons(configData, side) {
  const polygons = configData?.[getCropConfigKey(side)];
  if (!Array.isArray(polygons)) {
    return [];
  }
  return polygons.filter(
    (polygon) => Array.isArray(polygon) && polygon.length >= 3,
  );
}

function hasCropMaskConfigured(configData, side) {
  return getCropPolygons(configData, side).length > 0;
}

export function getPrimaryCropPolygon(configData, side) {
  const polygons = getCropPolygons(configData, side);
  if (polygons.length === 0) {
    return [];
  }
  return polygons[0].map(([x, y]) => [x, y]);
}

export function areCropPolygonsEqual(pointsA, pointsB) {
  return JSON.stringify(pointsA || []) === JSON.stringify(pointsB || []);
}

export function resolveCropImageDimensions(cycleEntry) {
  const width = Number(cycleEntry?.image_width) || 1000;
  const height = Number(cycleEntry?.image_height) || 1000;
  return {
    processedWidth: width,
    processedHeight: height,
    originalWidth: Math.max(width, height),
    originalHeight: Math.min(width, height),
  };
}

export function buildCropAzureOutput(configData, side, polygons) {
  const key = getCropConfigKey(side);
  return {
    ...configData,
    [key]: Array.isArray(polygons) ? polygons : [],
  };
}

export function formatCropMaskStatus(t, configData, side) {
  if (!hasCropMaskConfigured(configData, side)) {
    return t("installation_mask_crop_empty");
  }
  return t("installation_mask_crop_configured");
}

export function getMostRecentLogEntry(cycleEntries) {
  const sortedEntries = (cycleEntries || [])
    .slice()
    .sort((a, b) => String(b.name).localeCompare(String(a.name)));

  const entriesWithLog = sortedEntries.filter((entry) => entry?.log);
  if (entriesWithLog.length === 0) {
    return sortedEntries[0] || null;
  }

  return entriesWithLog.reduce((latest, current) => {
    const latestDate = new Date(latest.matchingCrontabDateTime);
    const currentDate = new Date(current.matchingCrontabDateTime);
    return currentDate > latestDate ? current : latest;
  }, entriesWithLog[0]);
}

export function getRecentRawImageEntry(cycleEntries) {
  const withImages = (cycleEntries || []).filter(
    (item) => item.log && (item.leftImage || item.rightImage),
  );
  if (withImages.length === 0) {
    return null;
  }
  return withImages.reduce((latest, current) => {
    const extractDateTime = (name) =>
      new Date(name.split("-camera-")[0].replace("_", ":").replace("_", ":"));
    return extractDateTime(latest.name) > extractDateTime(current.name)
      ? latest
      : current;
  });
}

export function getRecentRawImageUrl(
  routeCameraId,
  cycleEntries,
  side = "left",
) {
  const entry = getRecentRawImageEntry(cycleEntries);
  if (!entry?.name) {
    return "";
  }
  const suffix = side === "right" ? "-R" : "-L";
  return buildRawImageUrl(routeCameraId, entry.name, suffix);
}

export function buildRawImageUrl(cameraId, imageName, sideSuffix) {
  const baseId = getBaseCameraId(cameraId);
  return (
    "https://cameraanalysis.blob.core.windows.net/camera-analysis/camera-" +
    baseId +
    "/raw/" +
    imageName +
    sideSuffix +
    "-raw.jpeg" +
    AZURE_RAW_IMAGE_QUERY
  );
}
